import { Tag } from '@nimbus-ds/components';
import { TruckIcon } from '@nimbus-ds/icons';
import { MenuButton } from '@nimbus-ds/patterns';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import ShowByRole from 'App/components/ShowByRole';
import { useHandleMenuClick } from 'App/hooks';
import { useGetCountry, useGetTags } from 'domains/Auth/hooks';
import { useNuvemApps } from 'domains/PartnersApps/pages/NuvemAppPage/hooks';
import {
  ENVIO_NUBE_ADMIN_AR,
  ENVIO_NUBE_ADMIN_MX,
} from '../../../../../../featuresFlags';

interface AppsNuvemEnvioMenuProps {
  isMobile?: boolean;
}

const PATHS = {
  BR: '/apps/nuvemenvio',
  MX: '/apps/envionube/mx',
  AR: '/apps/envionube/ar',
};

function NuvemEnvioMenu({ isMobile }: Readonly<AppsNuvemEnvioMenuProps>) {
  const { t } = useTranslation('common');
  const { pathname, hash } = useLocation();
  const { nuvemApps } = useNuvemApps();
  const country = useGetCountry();
  const { handleClickItem, handleClickAccordion } = useHandleMenuClick();
  const tags = useGetTags();
  const isTaggedStore =
    tags.includes(ENVIO_NUBE_ADMIN_AR) || tags.includes(ENVIO_NUBE_ADMIN_MX);

  const APP_BY_COUNTRY = {
    MX: nuvemApps?.envionubemx?.installed || tags.includes(ENVIO_NUBE_ADMIN_MX),
    AR: nuvemApps?.envionubear?.installed || tags.includes(ENVIO_NUBE_ADMIN_AR),
    BR: nuvemApps.nuvemenvio !== undefined,
  };

  const hasNuvemEnvio = APP_BY_COUNTRY[country];

  const pathByCountry = PATHS[country];

  const dashboard = {
    children: `${t('menu.manage.nuvemenvio.dashboard')}`,
    onClick: handleClickItem,
    active: !hash.includes(`#/billing`),
    href: `${pathByCountry}/#/dashboard`,
  };

  const billing = {
    children: `${t('menu.manage.nuvemenvio.billing')}`,
    onClick: handleClickItem,
    active: hash.includes(`#/billing`),
    label: t('menu.new'),
    href: `${pathByCountry}/#/billing`,
  };

  const MENU_BY_COUNTRY = {
    MX: [],
    AR: [],
    BR: [dashboard, billing],
  };

  const menuNuvemEnvio: {
    children: string;
    onClick: () => void;
    active: boolean;
    href: string;
    label?: string;
  }[] = MENU_BY_COUNTRY[country] ?? [];

  const dashboarOrders = [
    {
      ...dashboard,
      active: hash.includes(`#/dashboard`) || pathname === pathByCountry,
    },
  ];

  const itemsNuvemEnvio = [...menuNuvemEnvio];
  return (
    <>
      <ShowByRole includeRoles={['full']}>
        {hasNuvemEnvio && (
          <MenuButton.Accordion
            contentid="content-nuvemenvio"
            menuButton={{
              id: 'control-nuvemenvio',
              label: t('menu.maximize.nuvemEnvio'),
              startIcon: TruckIcon,
              'aria-controls': 'content-nuvemenvio',
              onClick: handleClickAccordion,
              ...(isTaggedStore && {
                children: <Tag appearance="primary">{t('menu.new')}</Tag>,
              }),
            }}
            open={pathname.includes(pathByCountry)}
            active={pathname.includes(pathByCountry)}
            as={Link}
            to={pathByCountry}
          >
            {itemsNuvemEnvio.map((item, index) => (
              <MenuButton
                key={index}
                as={Link}
                label={item.children}
                onClick={item.onClick}
                to={item.href}
                active={item.active}
              >
                {!!item.label && isMobile && (
                  <Tag appearance="primary">{item.label}</Tag>
                )}
              </MenuButton>
            ))}
          </MenuButton.Accordion>
        )}
      </ShowByRole>
      <ShowByRole excludeRoles={['full']} includeRoles={['nuvem_envio']}>
        {hasNuvemEnvio && (
          <MenuButton.Accordion
            contentid="content-nuvemenvio-dashboard"
            menuButton={{
              id: 'control-nuvemenvio-dashboard',
              label: t('menu.maximize.nuvemEnvio'),
              startIcon: TruckIcon,
              'aria-controls': 'content-nuvemenvio-dashboard',
              onClick: handleClickAccordion,
              ...(isTaggedStore && {
                children: <Tag appearance="primary">{t('menu.new')}</Tag>,
              }),
            }}
            open={pathname.includes(pathByCountry)}
            active={pathname.includes(pathByCountry)}
            as={Link}
            to={pathByCountry}
          >
            {dashboarOrders.map((item, index) => (
              <MenuButton
                key={index}
                as={Link}
                label={item.children}
                onClick={item.onClick}
                to={item.href}
                active={item.active}
              />
            ))}
          </MenuButton.Accordion>
        )}
      </ShowByRole>
    </>
  );
}

export default NuvemEnvioMenu;
